import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import { SearchableSelectInput, Modal } from '../../components';
import { CreateOffice } from '../CreateOffice';
import { Office } from '../../app/feathers/offices/Office';
import { useFeathers } from '../../app/util';
import { District } from '../../app/feathers/districts/District';
import { IconButton, Skeleton, CircularProgress } from '@mui/material';
import { Cancel, CheckCircle, OpenInNew, Undo } from '@mui/icons-material';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { SearchableSelectInputProps } from '../SearchableSelectInput';

interface OfficeCellProps {
  office?: Office | null;
  suggestCreationAs?: string;
  onOfficeChange?: (newOffice: Office) => void;
  onOfficeClear?: () => void;
  district: District | null;
  districtId?: string;
  loading?: boolean;
  confidence?: number;
  onChangeConfidence?: (confidence: number) => void;
  disableEditing?: boolean;
  saving?: boolean;
  suggestIssuesToCover?: boolean;
  showDefinedOfficeActions?: boolean;
}

type OfficeOption = { text: string; key: string; office: Office };
type OfficeSelectChangeEvent = { target?: { value?: OfficeOption } };
const OfficeSearchableSelectInput = SearchableSelectInput as unknown as React.FC<SearchableSelectInputProps<OfficeOption>>;

const OfficeCell: React.FC<OfficeCellProps> = ({ office, suggestCreationAs, onOfficeChange, onOfficeClear, district, districtId, loading, confidence, onChangeConfidence, disableEditing, saving, suggestIssuesToCover, showDefinedOfficeActions }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [officeOptions, setOfficeOptions] = useState<OfficeOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fetchedDistrict, setFetchedDistrict] = useState<District | null>(null);
  const feathers = useFeathers<Application<ServiceTypes>>();
  const searchDistrictId = district?._id ?? districtId;
  const seedDistrict = district ?? fetchedDistrict;

  useEffect(() => {
    if (!feathers || !isModalOpen || district || !districtId) return;

    let cancelled = false;

    (async () => {
      try {
        const result = await feathers.getService('districts').get(districtId);
        if (!cancelled) {
          setFetchedDistrict(result);
        }
      } catch (error) {
        console.error('Error loading district for office creation:', error);
        if (!cancelled) {
          setFetchedDistrict(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [district, districtId, feathers, isModalOpen]);

  const searchOffices = async (searchText: string) => {
    if (!feathers) return;
    
    setOptionsLoading(true);
    try {
      const offices = await feathers.getService('offices').find({ 
        query: {
          ...(searchText ? { name: { $search: searchText } } : {}),
          $sort: { name: 1 },
          ...(searchDistrictId ? { district: searchDistrictId } : {})
        }
      });
      
      const options = offices.data.map((officeData: Office) => ({ 
        text: officeData.name, 
        key: officeData.key, 
        office: officeData 
      }));
      
      setOfficeOptions(options);
    } catch (error) {
      console.error('Error searching offices:', error);
      setOfficeOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleOfficeChange = (selectedOption: OfficeSelectChangeEvent) => {
    if (selectedOption && selectedOption.target?.value && onOfficeChange) {
      onOfficeChange(selectedOption.target.value.office);
    }
    setIsEditing(false);
  };

  const handleClick = () => {
    // If there's no office but there's a suggestCreationAs value, open modal
    if (!office && suggestCreationAs) {
      setIsModalOpen(true);
      return;
    }
    
    setIsEditing(true);
    // Load initial options when editing starts
    searchOffices('');
  };

  const handleCreateOffice = (newOffice: Office) => {
    setIsModalOpen(false);
    if (onOfficeChange) {
      onOfficeChange(newOffice);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (loading || saving) {
    // mui skeleton loading
    return <Skeleton variant="text" width={100} height={24} />;
  }

  if (disableEditing && !showDefinedOfficeActions) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" style={{ fontWeight: '500' }}>
          {office?.name || '-'}
        </Typography>
      </div>
    );
  }

  if (isEditing || (!office && !suggestCreationAs)) {
    return (
      <EditingContainer>
        <OfficeSearchableSelectInput
          onChangeSearchText={searchOffices}
          value={null}
          onChange={handleOfficeChange}
          options={officeOptions}
          optionsLoading={optionsLoading}
          style={{ 
            minWidth: '120px',
          }}
          placeholder="Search offices..."
        />
        {(office || suggestCreationAs) && (
          <IconButton
            onClick={() => setIsEditing(false)}
            size="small"
            title={suggestCreationAs ? 'Return to create office suggestion' : 'Return to original office'}
            aria-label={
              suggestCreationAs
                ? 'Return to create office suggestion'
                : `Return to original office ${office?.name || ''}`.trim()
            }
            style={{ marginLeft: '4px' }}
          >
            <Undo fontSize="small" />
          </IconButton>
        )}
      </EditingContainer>
    );
  }

  // Show yellow tile for suggested office creation
  if (!office && suggestCreationAs) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center' }}>  
          <SuggestedOfficeContainer onClick={handleClick}>
            <Typography variant="body2" style={{ color: '#8B4513' }}>
              Create <span style={{ fontWeight: '700',  color: '#8B4513' }}>{suggestCreationAs}</span>
            </Typography>
          </SuggestedOfficeContainer>
          <IconButton onClick={() => {
            setIsEditing(true);
            searchOffices('');
          }} style={{ marginLeft: '4px' }} size="small">
            <Cancel fontSize="small" />
          </IconButton>
        </div>
        {isModalOpen && (
          <Modal onClose={handleCloseModal}>
            <CreateOffice
              onClose={handleCloseModal}
              onCreate={handleCreateOffice}
              seedState={seedDistrict}
              seedName={suggestCreationAs}
              suggestIssuesToCover={suggestIssuesToCover}
            />
          </Modal>
        )}
      </>
    );
  }

  const displayName = office?.name || '-';

  // Show checkmark for low confidence labels when office is provided
  const showConfidenceCheckmark = office && confidence !== undefined && confidence < 0.5;

  const handleConfidenceConfirm = () => {
    if (onChangeConfidence) {
      onChangeConfidence(1.0);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <OfficeValueContainer>
        <OfficeContainer
          onClick={!disableEditing && !saving ? handleClick : undefined}
          $isLowConfidence={showConfidenceCheckmark || false}
          $isSaving={saving || false}
          $isEditable={!disableEditing}
        >
          <Typography variant="body2" style={{ fontWeight: '500', opacity: saving ? 0.6 : 1 }}>
            {displayName}
          </Typography>
        </OfficeContainer>
        {office && onOfficeClear && !disableEditing && !saving && (
          <ClearOfficeButton
            aria-label={`Clear ${displayName}`}
            title="Clear office"
            onClick={event => {
              event.stopPropagation();
              onOfficeClear();
            }}
            size="small"
          >
            <Cancel sx={{ fontSize: '14px' }} />
          </ClearOfficeButton>
        )}
      </OfficeValueContainer>
      {showDefinedOfficeActions && office && (
        <IconButton
          component="a"
          href={`/offices/${office.key}`}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          title="Open office details"
          aria-label={`Open ${displayName} details`}
        >
          <OpenInNew style={{ fontSize: '16px' }} />
        </IconButton>
      )}
      {saving && (
        <CircularProgress size={16} style={{ marginLeft: '4px' }} />
      )}
      {showConfidenceCheckmark && !saving && (
        <IconButton 
          onClick={handleConfidenceConfirm} 
          style={{ marginLeft: '4px' }} 
          size="small"
          title="Confirm this office (set confidence to 100%)"
        >
          <CheckCircle fontSize="small" style={{ color: '#1976d2' }} />
        </IconButton>
      )}
    </div>
  );
};

interface OfficeContainerProps {
  $isLowConfidence?: boolean;
  $isSaving?: boolean;
  $isEditable?: boolean;
}

const OfficeValueContainer = styled.div`
  display: inline-flex;
  position: relative;

  &:hover button,
  &:focus-within button {
    opacity: 1;
    pointer-events: auto;
  }
`;

const ClearOfficeButton = styled(IconButton)`
  position: absolute !important;
  top: -8px;
  right: -8px;
  z-index: 1;
  width: 20px;
  height: 20px;
  padding: 2px !important;
  opacity: 0;
  pointer-events: none;
  background-color: white !important;
  transition: opacity 120ms ease;
`;

const OfficeContainer = styled.div<OfficeContainerProps>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: ${props => props.$isLowConfidence ? '#fff3e0' : '#e3f2fd'};
  border: ${props => props.$isLowConfidence ? '1px solid #ffb74d' : 'none'};
  border-radius: 12px;
  max-width: 200px;
  cursor: ${props => props.$isSaving ? 'not-allowed' : (props.$isEditable ? 'pointer' : 'default')};
  opacity: ${props => props.$isSaving ? 0.6 : 1};
  
  &:hover {
    background-color: ${props => !props.$isEditable || props.$isSaving ? (props.$isLowConfidence ? '#fff3e0' : '#e3f2fd') : (props.$isLowConfidence ? '#ffe0b2' : '#bbdefb')};
  }
`;

const SuggestedOfficeContainer = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 12px;
  max-width: 200px;
  cursor: pointer;
  
  &:hover {
    background-color: #ffeaa7;
  }
`;

const EditingContainer = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 200px;
`;

export { OfficeCell };

